/**
 * Assessment Eligibility Predicate
 *
 * ╔══════════════════════════════════════════════════════════════════╗
 * ║  CRITICAL INVARIANT: ASSESSMENT = MULTIPLE CHOICE ONLY         ║
 * ║                                                                ║
 * ║  The assessment system EXCLUSIVELY uses multiple_choice         ║
 * ║  questions. This is a fundamental design decision, NOT a       ║
 * ║  temporary limitation. Reasons:                                ║
 * ║                                                                ║
 * ║  1. Objective, automated grading (no ambiguity)                ║
 * ║  2. Fair cognitive assessment (no visual/interaction bias)     ║
 * ║  3. Deterministic scoring (same answer = same score)           ║
 * ║  4. CITO-aligned testing methodology                           ║
 * ║  5. Professional exam-like experience                          ║
 * ║                                                                ║
 * ║  DO NOT add other question types to assessment eligibility.    ║
 * ║  Interactive types (fill_in, clock, money, ordering, etc.)     ║
 * ║  belong in PRACTICE mode only.                                 ║
 * ╚══════════════════════════════════════════════════════════════════╝
 *
 * ARCHITECTURE v4.0 — Policy Engine is single source of truth.
 * DDD co-locatie: moved from components/assessment/utils/ in s59.
 */

import type { Question } from '../../content'
import { isAssessmentEligibleType } from '../../governance'

/**
 * Check if a question is eligible for assessment use.
 * Three-layer enforcement: policy → MC guard → structural validation.
 */
export function isAssessmentEligible(question: Question): boolean {
  const questionType = question.questionType || 'multiple_choice'
  if (!isAssessmentEligibleType(questionType)) return false
  if (questionType !== 'multiple_choice') return false
  if (!question.options || question.options.length !== 4) return false
  if (typeof question.correctAnswer !== 'number') return false
  if (
    question.correctAnswer < 0 ||
    question.correctAnswer >= question.options.length
  )
    return false
  return true
}

export function getIneligibilityReason(question: Question): string | null {
  const questionType = question.questionType || 'multiple_choice'
  if (!isAssessmentEligibleType(questionType))
    return `Question type '${questionType}' is not assessment-eligible per policy`
  if (questionType !== 'multiple_choice')
    return `Question type '${questionType}' is not assessment-eligible (assessment uses multiple_choice ONLY)`
  if (!question.options || question.options.length !== 4)
    return `Has ${question.options?.length || 0} options, needs exactly 4`
  if (typeof question.correctAnswer !== 'number')
    return 'correctAnswer must be a number (option index)'
  if (
    question.correctAnswer < 0 ||
    question.correctAnswer >= (question.options?.length || 0)
  )
    return `correctAnswer index ${question.correctAnswer} out of range [0, ${(question.options?.length || 1) - 1}]`
  return null
}

export function filterAssessmentEligible(questions: Question[]): Question[] {
  return questions.filter(isAssessmentEligible)
}

export function getEligibilityStats(questions: Question[]): {
  total: number
  eligible: number
  ineligible: number
  percentage: number
  reasons: Record<string, number>
} {
  const total = questions.length
  const eligible = questions.filter(isAssessmentEligible).length
  const ineligible = total - eligible
  const reasons: Record<string, number> = {}
  questions.forEach((q) => {
    if (!isAssessmentEligible(q)) {
      const reason = getIneligibilityReason(q) || 'Unknown'
      reasons[reason] = (reasons[reason] || 0) + 1
    }
  })
  return {
    total,
    eligible,
    ineligible,
    percentage: total > 0 ? Math.round((eligible / total) * 100) : 0,
    reasons,
  }
}
